import logger from '../log';
import '../common/utils/polyfills';
import {
  HttpRequest,
  HttpResponse,
  RequestInterceptor,
  ResponseMapper
} from './interfaces';
import composeResponseMappers from './composeResponseMappers';
import { FetchFailed } from './errors';
import InterceptorType from './InterceptorType';
import InterceptorName from './InterceptorName';
import EventEmitter from '../common/EventEmitter';
import { InternalEvents, RequestIngestData } from '../events';
import fetchRequest from './fetchRequest';
import ContentConfig from '../config/ContentConfig';
import getUrlPrefix from '../ecdn11/getUrlPrefix';
import isRangeRequest from './utils/isRangeRequest';
import { LocalAgentFetchFailed, WontIntercept } from './errors';
import { checkAgentVersionGE, getAgentVersion } from '../util/checkVersion';

const log = logger.getLogger(__filename);
const { REQUEST_INGEST } = InternalEvents;

export default class LocalAgentInterceptor implements RequestInterceptor {
  responseMapper: ResponseMapper | undefined;
  readonly interceptorType = InterceptorType.HTTP;
  readonly interceptorName = InterceptorName.LOCAL_AGENT;
  eventEmitter: EventEmitter;
  modifiedBaseUrl: string;
  playGuid: string;

  constructor(opts: {
    contentId: string;
    tenantId: string;
    playGuid: string;
    token: string;
    config: ContentConfig;
  }) {
    this.eventEmitter = EventEmitter.getInstance();

    this.modifiedBaseUrl = `${getUrlPrefix(opts.config)}:${
      opts.config.agent.port
    }/tenant/${opts.tenantId}/content/${opts.contentId}`;
    this.playGuid = opts.playGuid;
  }

  modifyRequest(request: HttpRequest): HttpRequest {
    const modifiedUrl = new URL(this.modifiedBaseUrl);
    const origUrl = new URL(request.url);
    const origParams = origUrl.searchParams;
    const url = `${origUrl.origin}${origUrl.pathname}`;
    // set original params
    for (const [key, value] of origParams.entries()) {
      modifiedUrl.searchParams.append(key, value);
    }

    modifiedUrl.searchParams.set('url', url);
    if (checkAgentVersionGE('11.2')) {
      request.headers.set('X-Kollective-Playguid', this.playGuid);
    } else {
      log.info(
        'Not using header for playGuid as agent version is too low.',
        `Minimum 11.2; Current Version ${getAgentVersion()};`
      );
      modifiedUrl.searchParams.set('playguid', this.playGuid);
    }
    return { ...request, modifiedUrl: modifiedUrl.toString() };
  }

  registerResponseMapper(mapper: ResponseMapper): void {
    if (this.responseMapper) {
      this.responseMapper = composeResponseMappers(this.responseMapper, mapper);
    } else {
      this.responseMapper = mapper;
    }
  }

  shouldIntercept(request: HttpRequest): boolean {
    return !isRangeRequest(request.headers);
  }

  async handle(request: HttpRequest): Promise<HttpResponse> {
    const modified = this.modifyRequest(request);
    try {
      if (!this.shouldIntercept(request)) {
        return Promise.reject(new WontIntercept());
      }
      const response = await fetchRequest(modified);
      if (response.status >= 400) {
        // Drop back to other interceptors.
        return Promise.reject(new LocalAgentFetchFailed(request, response));
      }
      const data = response.body;
      // Send the response as a message to allow for caching and transfer records
      // Need to clone array buffer for caching.
      if (data && (data as ArrayBuffer).byteLength) {
        const { url, modifiedUrl } = modified;
        log.debug('From 11 Agent', (data as ArrayBuffer).byteLength, {
          response,
          url,
          modifiedUrl
        });
      }
      // we only want to cache this data
      // we also only want to original request in the event.
      const eventData: RequestIngestData =
        data && (data as ArrayBuffer).byteLength
          ? {
              type: REQUEST_INGEST,
              data: {
                ...response,
                body: (data as ArrayBuffer).slice(0)
              },
              cacheOnly: true
            }
          : { type: REQUEST_INGEST, data: response, cacheOnly: true };
      this.eventEmitter.dispatch(eventData);
      return this.responseMapper ? this.responseMapper(response) : response;
    } catch (e) {
      // notify failure
      const error = { request, modifyRequest: modified, error: e as Error };
      this.eventEmitter.dispatch({ type: REQUEST_INGEST, error });
      return Promise.reject(new FetchFailed(modified, e as Error));
    }
  }
}
